/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { useParams } from 'react-router-dom';

// Component imports
import { Button, Col, Empty, Form, Input, Row, Select, Space, Spin, Typography } from 'antd';
import { InfoCircleOutlined, SearchOutlined } from '@ant-design/icons';
import CustomInputNumber from './Fields/CustomInputNumber';
import EntryPointSelect from './Fields/EntryPointSelect';
import BotAvatar from '../UI/BotAvatar';

// Misc imports
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { ACTIVITY_TYPES, LANGUAGE_OPTIONS } from '../../constants/main';
import { PLUGIN_CATEGORY } from '../../constants/integrations';
import is from '../../utils/is';
import { useCloneAutomatedTestMutation } from '../../store/automatedTests/automatedTestsApi';

const AutomatedAgentTestsCloneForm = ({
  agentTestToClone,
  onSuccess,
  onCancel
}) => {
  const [form] = Form.useForm();
  const { orgId } = useParams();

  const { data: bots, isLoading: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId }, !orgId);
  const [cloneAutomatedTest, { isLoading: isCloningAutomatedTest }] = useCloneAutomatedTestMutation();
  const [selectedTenantId, setSelectedTenantId] = useState(agentTestToClone.examinedTenantId);
  const allActivityTypes = Object.values(ACTIVITY_TYPES);

  useEffect(() => {
    form.resetFields();
  }, [agentTestToClone]);

  const onSubmit = async (values) => {

    try {
      await cloneAutomatedTest({ orgId, id: agentTestToClone.id, data: values });
      onSuccess();
    } catch (e) {
      console.error(e);
    }
  };
  return (
    <Fragment>
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={{
          ...(agentTestToClone || {}),
          numOfTrials: agentTestToClone?.numOfTrials || 5,
          name: `${agentTestToClone?.name} Clone`,
          profile: {
            ...(agentTestToClone?.profile || {}),
            constraints: {
              ...(agentTestToClone?.profile?.constraints || {}),
              maxNumMessages: agentTestToClone?.profile?.constraints?.maxNumMessages || 5
            }
          }
        }}
        onValuesChange={(changedValues, allValues) => {
          if ('examinedTenantId' in changedValues) {
            form.setFieldsValue({ flowEntryPoint: null });
            setSelectedTenantId(changedValues.examinedTenantId);
          }
        }}
        className="py-4"
      >
        <Row>
          <Col xs={24}>
            <Form.Item name="name" label="Test Name" rules={[{ required: true, message: 'Test Name field is required' }]}>
              <Input placeholder="Enter test name" />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea placeholder="Enter short description" />
            </Form.Item>
            <Form.Item name="examinedTenantId" label="Agent" rules={[{ required: true, message: 'Agent field is required' }]}>
              <Select
                allowClear
                className="fs-base w-100"
                onClear={() => {
                  form.resetFields();
                }}
                getPopupContainer={triggerNode => triggerNode.parentElement}
                size="large"
                notFoundContent={
                  <Row className="p-4" justify="center" align="middle">
                    {
                      isFetchingBots ? (
                        <Spin />
                      ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No agents found')} />
                      )
                    }
                  </Row>
                }
                placeholder={t('Select an Agent')}
                showSearch
                filterOption={(input, option) =>
                  bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {
                  (bots?.items || []).filter(bot => bot.plugins.some(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING)).map(bot => (
                    <Select.Option key={bot.id} value={bot.id}>
                      <BotAvatar bot={bot} avatarSize={28} />
                    </Select.Option>
                  ))
                }
              </Select>
            </Form.Item>
            <Form.Item name="languageCode" label="Language" rules={[{ required: true, message: 'Language field is required' }]}>
              <Select
                showSearch
                allowClear
                filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
                placeholder={<Space size={4}><SearchOutlined className="text-secondary" /> {t('Search available languages')}</Space>}
                options={LANGUAGE_OPTIONS
                  .map(language => ({
                    label: <Space size={4}>{LANGUAGE_OPTIONS.find(lang => lang.key === language.key)?.icon} {language.label}</Space>,
                    value: language.key,
                    searchValue: language.label
                  }))
                }
              />
            </Form.Item>
            <Form.Item name="flowEntryPoint" label="Flow" rules={[{ required: true, message: 'Flow field is required' }]}>
              <EntryPointSelect
                botId={selectedTenantId}
                allowClear={true}
                activityTypesToDisplay={allActivityTypes}
                disabled={!selectedTenantId}
              />
            </Form.Item>
            <Form.Item
              name="numOfTrials"
              label="Sessions"
              rules={[{ required: true, message: 'Sessions field is required' }]}
              tooltip={{
                title: (
                  <Typography.Text type="secondary">
                    {
                      t('This test runs multiple times, with each run recorded as a separate agent session. This value indicates the total number of sessions to be generated.')
                    }
                  </Typography.Text>
                ),
                icon: <InfoCircleOutlined />
              }}
            >
              <CustomInputNumber
                min={1}
                step={1}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className="d-flex justify-content-center">
          <Col xs={12} className="d-flex justify-content-center">
            <Button
              className="w-90"
              onClick={onCancel}
              size="large"
              block
              disabled={isCloningAutomatedTest}
            >
              {t('Cancel')}
            </Button>
          </Col>
          <Col xs={12} className="d-flex justify-content-center">
            <Button
              className="w-90"
              type="primary"
              size="large"
              htmlType="submit"
              block
              loading={isCloningAutomatedTest}
              disabled={isCloningAutomatedTest}
            >
              {t('Clone')}
            </Button>
          </Col>
        </Row>
      </Form>
    </Fragment>
  );
};

AutomatedAgentTestsCloneForm.propTypes = {
  agentTestToClone: PropTypes.object,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func
};

export default AutomatedAgentTestsCloneForm;
